import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { chatWithGemini, GeminiMessage } from "@/lib/gemini";
import { getIntegration } from "@/lib/integrations";
import { confirmDeflection } from "@/lib/firestore";
import { Spinner, toast } from "@/components/shared";
import { useBranding } from "@/lib/branding";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { AiIntegrationConfig, AiProviderId } from "@/types";

interface ChatMsg { role: "user" | "ai"; text: string; id: number }
let msgId = 0;

const SUGGESTIONS = [
  "My VPN isn't working",
  "Computer is running slow",
  "Can't access my email",
  "I need software installed",
  "Forgot my password",
  "Printer is offline",
];

const PROVIDER_LABEL: Record<AiProviderId, string> = {
  gemini: "Gemini",
  openai: "OpenAI",
  anthropic: "Claude",
  openrouter: "OpenRouter",
};

interface LocationState {
  seedMessage?: string;
  deflectionId?: string;
}

export default function AiChat() {
  useDocumentTitle("AI Assistant");
  const navigate = useNavigate();
  const location = useLocation();
  const seedMessage = (location.state as LocationState | null)?.seedMessage;
  const deflectionId = (location.state as LocationState | null)?.deflectionId;
  const [deflectionDone, setDeflectionDone] = useState(false);

  const markFixed = async () => {
    if (deflectionId && !deflectionDone) {
      try {
        await confirmDeflection(deflectionId);
        setDeflectionDone(true);
        toast("Marked as resolved 🎉");
      } catch (e) {
        toast(`Could not save: ${(e as Error).message}`, "error");
      }
    }
    sendMessage("That fixed it, thanks!");
  };
  const branding = useBranding();
  const [provider, setProvider] = useState<AiProviderId>("gemini");
  const [messages, setMessages] = useState<ChatMsg[]>([{
    id: ++msgId, role: "ai",
    text: `Hi! I'm the ${branding.portalName} AI assistant. I can help troubleshoot common IT issues — VPN, email, password, hardware, software. If we can't sort it out here, I'll help you create a ticket. What's going on?`,
  }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<GeminiMessage[]>([]);
  const seededRef = useRef(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    getIntegration<AiIntegrationConfig>("ai").then((d) => {
      if (d.config.provider) setProvider(d.config.provider);
    }).catch(() => {});
  }, []);

  // If we arrived from the New Ticket form with seeded text, send it
  // as the first user message so the AI starts engaging immediately.
  useEffect(() => {
    if (!seedMessage || seededRef.current) return;
    seededRef.current = true;
    // Replace history with the seed before sending so it isn't sent twice.
    void sendMessage(seedMessage);
    // Clear router state so a refresh doesn't re-send.
    window.history.replaceState({}, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedMessage]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const sendMessage = async (text: string) => {
    if (!text.trim() || loading) return;
    setInput("");

    const userMsg: ChatMsg = { id: ++msgId, role: "user", text };
    setMessages((prev) => [...prev, userMsg]);
    setLoading(true);

    try {
      const reply = await chatWithGemini(history, text);
      const newHistory: GeminiMessage[] = [
        ...history,
        { role: "user", parts: [{ text }] },
        { role: "model", parts: [{ text: reply }] },
      ];
      setHistory(newHistory);
      setMessages((prev) => [...prev, { id: ++msgId, role: "ai", text: reply }]);
    } catch (err) {
      setMessages((prev) => [...prev, {
        id: ++msgId, role: "ai",
        text: `I'm having trouble reaching the AI service: ${(err as Error).message}. You can submit a ticket and the team will help.`,
      }]);
    }
    setLoading(false);
    inputRef.current?.focus();
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  };

  const autoResize = (el: HTMLTextAreaElement) => {
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 100) + "px";
  };

  const createTicketFromChat = () => {
    const userMessages = messages.filter((m) => m.role === "user");
    const firstUser = userMessages[0]?.text ?? "Help needed";
    const title = firstUser.length > 80 ? firstUser.slice(0, 77) + "…" : firstUser;
    const transcript = messages.slice(1) // skip the canned intro
      .map((m) => `${m.role === "user" ? "Me" : "AI"}: ${m.text}`)
      .join("\n\n");
    const description = `From AI assistant chat — please review and follow up.\n\n${transcript}`;
    navigate("/tickets/new", {
      state: { prefill: { title, description, origin: "ai-chat", originChat: transcript } },
    });
  };

  const initials = PROVIDER_LABEL[provider].slice(0, 2).toUpperCase();
  const canCreateTicket = messages.some((m) => m.role === "user");

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "var(--bg)" }}>
      <div style={{ padding: "14px 18px", background: "var(--surface)", borderBottom: "0.5px solid var(--border)", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 36, height: 36, background: "var(--gem)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "white" }}>{initials}</div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: -.3 }}>AI Assistant</div>
            <div style={{ fontSize: 12, color: "var(--text2)" }}>Powered by {PROVIDER_LABEL[provider]} · Self-service support</div>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
            {canCreateTicket && (
              <button className="btn btn-secondary btn-sm" onClick={createTicketFromChat}>
                📋 Create ticket from chat
              </button>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "var(--green-t)" }}>
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--green)", animation: "pulse 2s ease-in-out infinite" }} />
              Online
            </div>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "14px 18px", display: "flex", flexDirection: "column", gap: 10 }}>
        {messages.map((msg) => (
          <div key={msg.id} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
            <div style={{
              maxWidth: "82%", padding: "10px 13px", borderRadius: 14, fontSize: 13.5, lineHeight: 1.55,
              borderBottomRightRadius: msg.role === "user" ? 4 : 14,
              borderBottomLeftRadius: msg.role === "ai" ? 4 : 14,
              background: msg.role === "user" ? "var(--blue)" : "var(--surface)",
              color: msg.role === "user" ? "white" : "var(--text)",
              border: msg.role === "ai" ? "0.5px solid var(--border)" : "none",
              animation: "fadeIn 0.2s ease",
            }}>
              {msg.role === "ai" && (
                <div style={{ fontSize: 10, fontWeight: 700, color: "var(--gem)", textTransform: "uppercase", letterSpacing: .5, marginBottom: 4 }}>
                  AI Assistant
                </div>
              )}
              {msg.role === "ai" ? (
                <div className="md">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
                </div>
              ) : (
                <p style={{ margin: 0, whiteSpace: "pre-wrap" }}>{msg.text}</p>
              )}

              {msg.role === "ai" && messages[messages.length - 1]?.id === msg.id && !loading && messages.length > 1 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 10 }}>
                  <button
                    onClick={markFixed}
                    style={{ fontSize: 12, padding: "5px 11px", border: "1px solid var(--green)", color: "var(--green-t)", background: "var(--green-l)", borderRadius: 20, cursor: "pointer", fontFamily: "var(--font)" }}
                  >
                    ✓ That fixed it
                  </button>
                  <button
                    onClick={() => sendMessage("Still not working, please suggest a different approach")}
                    style={{ fontSize: 12, padding: "5px 11px", border: "1px solid var(--border-med)", color: "var(--text)", background: "var(--surface)", borderRadius: 20, cursor: "pointer", fontFamily: "var(--font)" }}
                  >
                    Still not working
                  </button>
                  <button
                    onClick={createTicketFromChat}
                    style={{ fontSize: 12, padding: "5px 11px", border: "1px solid var(--gem)", color: "var(--gem-t)", background: "var(--gem-l)", borderRadius: 20, cursor: "pointer", fontFamily: "var(--font)" }}
                  >
                    📋 Create ticket from this chat
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div style={{ display: "flex" }}>
            <div style={{ padding: "10px 13px", background: "var(--surface)", border: "0.5px solid var(--border)", borderRadius: 14, borderBottomLeftRadius: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                {[0, 0.2, 0.4].map((delay, i) => (
                  <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--text3)", animation: `pulse 1.2s ${delay}s ease-in-out infinite` }} />
                ))}
              </div>
            </div>
          </div>
        )}

        {messages.length === 1 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => sendMessage(s)}
                style={{ fontSize: 12, padding: "6px 12px", border: "1px solid var(--gem)", color: "var(--gem)", background: "var(--surface)", borderRadius: 20, cursor: "pointer", fontFamily: "var(--font)", transition: "all .15s" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "var(--gem)"; e.currentTarget.style.color = "white"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "var(--surface)"; e.currentTarget.style.color = "var(--gem)"; }}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      <div style={{ padding: "10px 18px", background: "var(--surface)", borderTop: "0.5px solid var(--border)", display: "flex", gap: 8, alignItems: "flex-end", flexShrink: 0 }}>
        <textarea
          ref={inputRef}
          className="fi"
          style={{ flex: 1, borderRadius: 20, resize: "none", minHeight: 38, maxHeight: 100, padding: "8px 15px", lineHeight: 1.4 }}
          placeholder="Describe your issue…"
          value={input}
          onChange={(e) => { setInput(e.target.value); autoResize(e.target); }}
          onKeyDown={handleKey}
          rows={1}
        />
        <button
          onClick={() => sendMessage(input)}
          disabled={!input.trim() || loading}
          style={{ width: 36, height: 36, borderRadius: "50%", background: input.trim() && !loading ? "var(--gem)" : "var(--gray)", border: "none", cursor: input.trim() && !loading ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
        >
          {loading ? <Spinner size={14} color="var(--gem)" /> : (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="white"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
          )}
        </button>
      </div>
    </div>
  );
}
